import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PageNotFound from 'components/PageNotFound';
import { verifyToken, submitEmployeeData } from './publicAction';
import { Form, Input, Select, DatePicker, Row, Col, Button } from 'antd';
import moment from 'moment';
import { IEmployee } from 'types';
import { useAppSelector } from 'app/hooks';
import { selectConfiguration } from 'features/configuration/configurationSlice';

// Helper to get query params
type QueryParams = {
  token?: string;
};

function useQuery(): QueryParams {
  return React.useMemo(() => {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    return {
      token: params.get('token') || undefined,
    };
  }, []);
}

const FillDataPage: React.FC = () => {
  const { token } = useQuery();
  const dispatch = useDispatch();
  const [verifyStatus, setVerifyStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle',
  );
  const [employeeData, setEmployeeData] = React.useState<Partial<IEmployee>>({ relative: [] });
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const [dataConfiguration, setDataConfiguration] = React.useState<any>({});

  const validateRequiredFields = () => {
    const errors: string[] = [];

    // Thông tin cá nhân bắt buộc
    if (!employeeData.fullName?.trim()) errors.push('Họ và tên');
    if (!employeeData.citizenId?.trim()) errors.push('Số CMND/CCCD');
    if (!employeeData.cidIssuedDate) errors.push('Ngày cấp CMND/CCCD');
    if (!employeeData.cidPlaceOfIssue?.trim()) errors.push('Nơi cấp CMND/CCCD');
    if (!employeeData.dob) errors.push('Ngày sinh');
    if (employeeData.sex === undefined || employeeData.sex === null) errors.push('Giới tính');
    if (!employeeData.nationality?.trim()) errors.push('Quốc tịch');
    if (employeeData.ethnicity === undefined || employeeData.ethnicity === null)
      errors.push('Dân tộc');
    if (employeeData.religion === undefined || employeeData.religion === null)
      errors.push('Tôn giáo');
    if (employeeData.maritalStatus === undefined || employeeData.maritalStatus === null)
      errors.push('Tình trạng hôn nhân');
    if (!employeeData.email?.trim()) errors.push('Email cá nhân');
    if (!employeeData.phoneNumber?.trim()) errors.push('Số điện thoại');
    // Thông tin ngân hàng bắt buộc
    if (!employeeData.bankName?.trim()) errors.push('Tên ngân hàng');
    if (!employeeData.bankAccount?.trim()) errors.push('Số tài khoản');

    return errors;
  };

  const handleSubmit = async () => {
    // Validate required fields trước khi submit
    const validationErrors = validateRequiredFields();
    if (validationErrors.length > 0) {
      setSubmitError(`Vui lòng điền đầy đủ các trường bắt buộc: ${validationErrors.join(', ')}`);
      return;
    }

    setSubmitLoading(true);
    setSubmitSuccess(false);
    setSubmitError(null);
    try {
      await dispatch<any>(submitEmployeeData({ token: token!, data: employeeData })).unwrap();
      setSubmitSuccess(true);
    } catch (err: any) {
      setSubmitError(err?.message || 'Gửi thông tin thất bại');
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    if (token) {
      setVerifyStatus('loading');
      dispatch<any>(verifyToken(token))
        .unwrap()
        .then((res: any) => {
          if (!ignore) {
            setVerifyStatus('success');
            // Nếu backend trả về thông tin user, tự động set vào form
            if (res) {
              setEmployeeData((prev: any) => ({
                ...prev,
                ...res.employeeData,
                relative: res.employeeData.relative || prev.relative || [],
              }));
              setDataConfiguration(res.dataConfiguration);
            }
          }
        })
        .catch(() => {
          if (!ignore) setVerifyStatus('error');
        });
    }
    return () => {
      ignore = true;
    };
  }, [token, dispatch]);

  if (verifyStatus === 'loading') {
    return (
      <div
        style={{
          maxWidth: 500,
          margin: '40px auto',
          padding: 24,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <h2>Điền thông tin nhân viên mới</h2>
        <p>Đang xác thực...</p>
      </div>
    );
  }

  if (!token || verifyStatus === 'error') {
    return <PageNotFound />;
  }

  // Nếu thành công
  return (
    <div
      style={{
        maxWidth: 900,
        margin: '40px auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
      className="ant-form ant-form-vertical"
    >
      <h2>Điền thông tin nhân viên</h2>
      <Form layout="vertical">
        <h3
          style={{
            color: '#f1592a',
            borderBottom: '2px solid #f1592a',
            paddingBottom: 6,
            marginBottom: 24,
            fontWeight: 600,
          }}
        >
          Thông tin cá nhân
        </h3>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Họ và tên"
              required
              rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
            >
              <Input
                disabled
                value={employeeData.fullName || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, fullName: e.target.value })}
                placeholder="Nhập họ và tên"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Số CMND/CCCD"
              required
              rules={[{ required: true, message: 'Vui lòng nhập số CMND/CCCD' }]}
            >
              <Input
                value={employeeData.citizenId || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, citizenId: e.target.value })}
                placeholder="Nhập số CMND/CCCD"
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Ngày cấp"
              required
              rules={[{ required: true, message: 'Vui lòng chọn ngày cấp' }]}
            >
              <DatePicker
                value={employeeData.cidIssuedDate ? moment(employeeData.cidIssuedDate) : undefined}
                onChange={(date) =>
                  setEmployeeData({
                    ...employeeData,
                    cidIssuedDate: date ? date.format('YYYY-MM-DD') : '',
                  })
                }
                style={{ width: '100%' }}
                placeholder="Chọn ngày cấp"
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Nơi cấp"
              required
              rules={[{ required: true, message: 'Vui lòng nhập nơi cấp' }]}
            >
              <Input
                value={employeeData.cidPlaceOfIssue || ''}
                onChange={(e) =>
                  setEmployeeData({ ...employeeData, cidPlaceOfIssue: e.target.value })
                }
                placeholder="Nhập nơi cấp"
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Ngày sinh"
              required
              rules={[{ required: true, message: 'Vui lòng chọn ngày sinh' }]}
            >
              <DatePicker
                value={employeeData.dob ? moment(employeeData.dob) : undefined}
                onChange={(date) =>
                  setEmployeeData({
                    ...employeeData,
                    dob: date ? date.format('YYYY-MM-DD') : '',
                  })
                }
                style={{ width: '100%' }}
                placeholder="Chọn ngày sinh"
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Giới tính"
              required
              rules={[{ required: true, message: 'Vui lòng chọn giới tính' }]}
            >
              <Select
                value={
                  employeeData.sex !== undefined && employeeData.sex !== null
                    ? Number(employeeData.sex)
                    : undefined
                }
                onChange={(value) => setEmployeeData({ ...employeeData, sex: value })}
                placeholder="Chọn giới tính"
                options={
                  (dataConfiguration?.dataSex || []).map((item: any) => ({
                    label:
                      (item.label ?? item.name) === 'Male'
                        ? 'Nam'
                        : (item.label ?? item.name) === 'Female'
                        ? 'Nữ'
                        : item.label ?? item.name,
                    value: Number(item.value ?? item.id),
                  })) || []
                }
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Quốc tịch"
              required
              rules={[{ required: true, message: 'Vui lòng nhập quốc tịch' }]}
            >
              <Input
                value={employeeData.nationality || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, nationality: e.target.value })}
                placeholder="Nhập quốc tịch"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Dân tộc"
              required
              rules={[{ required: true, message: 'Vui lòng nhập dân tộc' }]}
            >
              <Select
                value={
                  employeeData.ethnicity !== undefined && employeeData.ethnicity !== null
                    ? Number(employeeData.ethnicity)
                    : undefined
                }
                onChange={(value) => setEmployeeData({ ...employeeData, ethnicity: value })}
                placeholder="Chọn dân tộc"
                options={(dataConfiguration?.dataEthnicity || []).map((item: any) => ({
                  label: item.label ?? item.name, // Nếu cần tiếng Việt thì map lại ở đây
                  value: Number(item.value ?? item.id),
                }))}
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item
              label="Tôn giáo"
              required
              rules={[{ required: true, message: 'Vui lòng nhập tôn giáo' }]}
            >
              <Select
                value={
                  employeeData.religion !== undefined && employeeData.religion !== null
                    ? Number(employeeData.religion)
                    : undefined
                }
                onChange={(value) => setEmployeeData({ ...employeeData, religion: value })}
                placeholder="Chọn tôn giáo"
                options={(dataConfiguration?.dataReligion || []).map((item: any) => ({
                  label: item.label ?? item.name, // Nếu cần tiếng Việt thì map lại ở đây
                  value: Number(item.value ?? item.id),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              label="Tình trạng hôn nhân"
              required
              rules={[{ required: true, message: 'Vui lòng chọn tình trạng hôn nhân' }]}
            >
              <Select
                value={
                  employeeData.maritalStatus !== undefined && employeeData.maritalStatus !== null
                    ? Number(employeeData.maritalStatus)
                    : undefined
                }
                onChange={(value) => setEmployeeData({ ...employeeData, maritalStatus: value })}
                placeholder="Chọn tình trạng hôn nhân"
                options={
                  (dataConfiguration?.dataMaritalStatus || []).map((item: any) => ({
                    label:
                      (item.label ?? item.name) === 'Single'
                        ? 'Độc thân'
                        : (item.label ?? item.name) === 'Married'
                        ? 'Đã kết hôn'
                        : (item.label ?? item.name) === 'Other'
                        ? 'Khác'
                        : item.label ?? item.name,
                    value: Number(item.value ?? item.id),
                  })) || []
                }
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item label="Email cá nhân" required>
              <Input
                value={employeeData.email || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, email: e.target.value })}
                placeholder="Nhập email cá nhân"
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Số điện thoại" required>
              <Input
                value={employeeData.phoneNumber || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, phoneNumber: e.target.value })}
                placeholder="Nhập số điện thoại"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item label="Địa chỉ thường trú">
              <Input
                value={employeeData.permanentAddress || ''}
                onChange={(e) =>
                  setEmployeeData({
                    ...employeeData,
                    permanentAddress: e.target.value,
                  })
                }
                placeholder="Nhập địa chỉ thường trú"
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Địa chỉ tạm trú">
              <Input
                value={employeeData.temporaryAddress || ''}
                onChange={(e) =>
                  setEmployeeData({
                    ...employeeData,
                    temporaryAddress: e.target.value,
                  })
                }
                placeholder="Nhập địa chỉ tạm trú"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item label="Số sổ bảo hiểm xã hội">
              <Input
                value={employeeData.socialInsuranceNumber || ''}
                onChange={(e) =>
                  setEmployeeData({ ...employeeData, socialInsuranceNumber: e.target.value })
                }
                placeholder="Nhập số sổ BHXH"
              />
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <Form.Item label="Mã số thuế cá nhân">
              <Input
                value={employeeData.taxCode || ''}
                onChange={(e) => setEmployeeData({ ...employeeData, taxCode: e.target.value })}
                placeholder="Nhập mã số thuế cá nhân"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Thông tin học vấn */}
      <h3
        style={{
          marginTop: 32,
          color: '#f1592a',
          fontWeight: 600,
          borderBottom: '2px solid #f1592a',
          paddingBottom: 4,
          marginBottom: 24,
        }}
      >
        Thông tin học vấn
      </h3>
      <Row gutter={16}>
        <Col md={8} xs={24}>
          <Form.Item label="Trường học">
            <Input
              value={employeeData.school || ''}
              onChange={(e) => setEmployeeData({ ...employeeData, school: e.target.value })}
              placeholder="Nhập trường học"
            />
          </Form.Item>
        </Col>
        <Col md={8} xs={24}>
          <Form.Item label="Chuyên ngành">
            <Input
              value={employeeData.schoolMajor || ''}
              onChange={(e) => setEmployeeData({ ...employeeData, schoolMajor: e.target.value })}
              placeholder="Nhập chuyên ngành"
            />
          </Form.Item>
        </Col>
        <Col md={8} xs={24}>
          <Form.Item label="Trình độ">
            <Select
              value={
                employeeData.degree !== undefined && employeeData.degree !== null
                  ? Number(employeeData.degree)
                  : undefined
              }
              onChange={(value) => setEmployeeData({ ...employeeData, degree: value })}
              placeholder="Chọn trình độ"
              options={
                (dataConfiguration?.dataDegree || []).map((item: any) => ({
                  label:
                    (item.label ?? item.name) === 'None'
                      ? 'Không'
                      : (item.label ?? item.name) === 'High School'
                      ? 'THPT'
                      : (item.label ?? item.name) === 'College'
                      ? 'Cao đẳng'
                      : (item.label ?? item.name) === 'University'
                      ? 'Đại học'
                      : item.label ?? item.name,
                  value: Number(item.value ?? item.id),
                })) || []
              }
            />
          </Form.Item>
        </Col>
        <Col md={8} xs={24}>
          <Form.Item label="Ngày bắt đầu">
            <DatePicker
              value={
                employeeData.trainingPeriodFrom
                  ? moment(employeeData.trainingPeriodFrom)
                  : undefined
              }
              onChange={(date) =>
                setEmployeeData({
                  ...employeeData,
                  trainingPeriodFrom: date ? date.format('YYYY-MM-DD') : '',
                })
              }
              style={{ width: '100%' }}
              placeholder="Chọn ngày bắt đầu"
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>
        <Col md={8} xs={24}>
          <Form.Item label="Ngày kết thúc">
            <DatePicker
              value={
                employeeData.trainingPeriodTo ? moment(employeeData.trainingPeriodTo) : undefined
              }
              onChange={(date) =>
                setEmployeeData({
                  ...employeeData,
                  trainingPeriodTo: date ? date.format('YYYY-MM-DD') : '',
                })
              }
              style={{ width: '100%' }}
              placeholder="Chọn ngày kết thúc"
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Thông tin ngân hàng */}
      <h3
        style={{
          marginTop: 32,
          color: '#f1592a',
          borderBottom: '2px solid #f1592a',
          paddingBottom: 6,
          marginBottom: 24,
          fontWeight: 600,
        }}
      >
        Thông tin ngân hàng
      </h3>
      <Row gutter={16}>
        <Col md={12} xs={24}>
          <Form.Item
            label="Tên ngân hàng"
            required
            rules={[{ required: true, message: 'Vui lòng nhập tên ngân hàng' }]}
          >
            <Input
              value={employeeData.bankName}
              onChange={(e) => setEmployeeData({ ...employeeData, bankName: e.target.value })}
              placeholder="Nhập tên ngân hàng"
            />
          </Form.Item>
        </Col>
        <Col md={12} xs={24}>
          <Form.Item
            label="Số tài khoản"
            required
            rules={[{ required: true, message: 'Vui lòng nhập số tài khoản' }]}
          >
            <Input
              value={employeeData.bankAccount || ''}
              onChange={(e) => setEmployeeData({ ...employeeData, bankAccount: e.target.value })}
              placeholder="Nhập số tài khoản"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Thông tin người thân */}
      <h3
        style={{
          marginTop: 32,
          color: '#f1592a',
          borderBottom: '2px solid #f1592a',
          paddingBottom: 6,
          marginBottom: 24,
          fontWeight: 600,
        }}
      >
        Thông tin người liên hệ
      </h3>
      {(employeeData.relative || []).map((item: any, idx: number) => (
        <Row gutter={16} key={idx} align="middle" style={{ marginBottom: 8 }}>
          <Col md={8} xs={24}>
            <Form.Item label="Họ tên người thân">
              <Input
                value={item.name || ''}
                onChange={(e) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].name = e.target.value;
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                placeholder="Nhập họ tên người thân"
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Quan hệ">
              <Select
                value={
                  typeof item.relationship === 'object'
                    ? item.relationship.id
                    : item.relationship !== undefined && item.relationship !== null
                    ? Number(item.relationship)
                    : undefined
                }
                onChange={(value) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].relationship = value;
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                placeholder="Chọn quan hệ"
                options={
                  (dataConfiguration?.dataRelationship || []).map((rel: any) => ({
                    label:
                      (rel.label ?? rel.name) === 'Father'
                        ? 'Bố'
                        : (rel.label ?? rel.name) === 'Mother'
                        ? 'Mẹ'
                        : (rel.label ?? rel.name) === 'Brother'
                        ? 'Anh trai'
                        : (rel.label ?? rel.name) === 'Sister'
                        ? 'Chị gái'
                        : (rel.label ?? rel.name) === 'Wife'
                        ? 'Vợ'
                        : (rel.label ?? rel.name) === 'Husband'
                        ? 'Chồng'
                        : (rel.label ?? rel.name) === 'Children'
                        ? 'Con'
                        : (rel.label ?? rel.name) === 'Friend'
                        ? 'Bạn'
                        : (rel.label ?? rel.name) === 'Other'
                        ? 'Khác'
                        : rel.label ?? rel.name,
                    value: Number(rel.value),
                  })) || []
                }
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Năm sinh">
              <DatePicker
                picker="year"
                value={item.yearOfBirth ? moment(item.yearOfBirth) : undefined}
                onChange={(date) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].yearOfBirth = date
                    ? date.startOf('year').format('YYYY-MM-DD')
                    : '';
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                style={{ width: '100%' }}
                placeholder="Chọn năm sinh"
                format="YYYY"
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Nghề nghiệp">
              <Input
                value={item.occupation || ''}
                onChange={(e) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].occupation = e.target.value;
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                placeholder="Nhập nghề nghiệp"
              />
            </Form.Item>
          </Col>
          <Col md={8} xs={24}>
            <Form.Item label="Email">
              <Input
                type="email"
                value={item.email || ''}
                onChange={(e) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].email = e.target.value;
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                placeholder="Nhập email"
              />
            </Form.Item>
          </Col>
          <Col md={6} xs={20}>
            <Form.Item label="Số điện thoại">
              <Input
                value={item.phoneNumber || ''}
                onChange={(e) => {
                  const newRelative = [...employeeData.relative];
                  newRelative[idx].phoneNumber = e.target.value;
                  setEmployeeData({ ...employeeData, relative: newRelative });
                }}
                placeholder="Nhập số điện thoại"
              />
            </Form.Item>
          </Col>
          <Col md={2} xs={4} style={{ textAlign: 'center' }}>
            <Button
              style={{ background: '#fff0e9', color: '#f1592a', borderColor: '#f1592a' }}
              size="small"
              onClick={() => {
                const newRelative = [...employeeData.relative];
                newRelative.splice(idx, 1);
                setEmployeeData({ ...employeeData, relative: newRelative });
              }}
            >
              Xóa
            </Button>
          </Col>
        </Row>
      ))}
      <Button
        type="dashed"
        style={{ marginTop: 8, borderColor: '#f1592a', color: '#f1592a', fontWeight: 600 }}
        onClick={() => {
          setEmployeeData({
            ...employeeData,
            relative: [
              ...(employeeData.relative || []),
              {
                name: '',
                relationship: '',
                yearOfBirth: '',
                occupation: '',
                email: '',
                phoneNumber: '',
              },
            ],
          });
        }}
        block
      >
        + Thêm người thân
      </Button>

      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <Button
          type="primary"
          className="ant-btn ant-btn-default"
          disabled={submitLoading || employeeData.userFilledData}
          onClick={handleSubmit}
        >
          {submitLoading ? 'Đang gửi...' : 'Gửi thông tin'}
        </Button>
        {submitSuccess && (
          <div style={{ color: 'green', marginTop: 16 }}>Gửi thông tin thành công!</div>
        )}
        {submitError && <div style={{ color: 'red', marginTop: 16 }}>{submitError}</div>}
      </div>
    </div>
  );
};

export default FillDataPage;
